import { usePlayer } from "@/context/PlayerContext";
import { useI18n } from "@/context/I18nContext";
import { Play, Pause, SkipBack, SkipForward, Volume2, Square, ChevronDown, Music, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { resolveImg } from "@/lib/api";
import PurchaseModal from "@/components/PurchaseModal";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AudioPlayer() {
  const { current, playing, progress, duration, togglePlay, skipNext, skipPrev, seek, volume, setVolume, pause, close, playTrack } = usePlayer();
  const { user } = useAuth();
  const [discovery, setDiscovery] = useState(null);   // a new fan's daily full listen (routers/discovery.py)
  const { t } = useI18n();
  const [showFull, setShowFull] = useState(false);
  const [buying, setBuying] = useState(null);
  // a paid song not bought yet: only the extract chosen by the artist plays (cut by the server)
  const isPreview = current && !current.owned && !current.is_free && current.price > 0 && !current.discovery;
  // each paid song: once in full per day (renewed every day) during the discovery period
  const canDiscover = isPreview && discovery?.eligible && !(discovery.listened_today || []).includes(current.id);
  // the real length of what is playing — the extract for a song not bought, the whole song otherwise
  // (before the sound has loaded: the extract's length for an extract, never the whole song's)
  const total = duration || (isPreview ? current?.preview_duration || 30 : current?.duration) || 0;

  useEffect(() => {
    const onEnd = (e) => {
      const tr = e.detail;
      toast.info(t.audio.previewEnded, {
        description: tr?.title, duration: 7000,
        action: tr ? { label: `Acheter · $${Number(tr.price).toFixed(2)}`, onClick: () => setBuying(tr) } : undefined,
      });
    };
    window.addEventListener("metazik:preview-end", onEnd);
    return () => window.removeEventListener("metazik:preview-end", onEnd);
  }, [t]);

  useEffect(() => {
    if (!isPreview || user?.role !== "viewer") return;
    api.get("/discovery/me").then((r) => setDiscovery(r.data)).catch(() => {});
  }, [isPreview, current?.id, user?.role]);

  const listenInFull = async (e) => {
    e?.stopPropagation?.();
    try {
      const { data } = await api.post(`/tracks/${current.id}/discovery`);
      setDiscovery(data);
      const full = { ...current, audio_url: data.audio_url, preview_audio_url: current.audio_url, discovery: true };
      playTrack(full, [full]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Indisponible pour le moment.");
    }
  };

  if (!current) return null;

  const handleStop = (e) => {
    e.stopPropagation();
    pause();
    seek(0);
  };

  const handleTogglePlay = (e) => {
    e.stopPropagation();
    togglePlay();
  };

  const handleClose = (e) => {
    e.stopPropagation();
    close();
  };

  return (
    <>
      {/* Mini Player Bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 glass-player cursor-pointer"
        onClick={() => setShowFull(true)}
        data-testid="audio-player"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-3 sm:gap-5">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="relative shrink-0">
              <img
                src={resolveImg(current.cover)}
                alt={current.title}
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover"
              />
              {playing && (
                <div className="absolute inset-0 rounded-lg flex items-end justify-center gap-0.5 bg-black/30">
                  <span className="eq-bar w-1 h-3 bg-[#00F2FE] rounded-sm" />
                  <span className="eq-bar w-1 h-3 bg-[#FF0050] rounded-sm" />
                  <span className="eq-bar w-1 h-3 bg-[#8A2BE2] rounded-sm" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white" data-testid="player-track-title">{current.title}</p>
              {/* the label sits on its own line, before the artist name: never cut by a long title */}
              <div className="flex items-center gap-1.5 min-w-0">
                {current.discovery && (
                  <span className="chip shrink-0 whitespace-nowrap bg-[#00F2FE]/20 text-[#00F2FE] text-[10px] py-0 px-2">Écoute gratuite</span>
                )}
                {isPreview && (
                  <span className="chip shrink-0 whitespace-nowrap bg-[#FF0050]/20 text-[#FF0050] text-[10px] py-0 px-2" data-testid="player-preview-chip">
                    {t.audio.previewLabel}{total ? ` · ${Math.round(total)} s` : ""}
                  </span>
                )}
                <p className="truncate text-xs text-zinc-400 min-w-0">{current.artist_name}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center center gap-1 sm:gap-2">
            <button onClick={(e) => { e.stopPropagation(); skipPrev(); }} className="p-2 text-zinc-300 hover:text-white"><SkipBack className="w-5 h-5" /></button>
            <button onClick={handleStop} className="p-2 text-zinc-300 hover:text-white"><Square className="w-5 h-5" /></button>
            <button onClick={handleTogglePlay} className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white text-black grid place-items-center active:scale-95 transition-transform">
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <button onClick={(e) => { e.stopPropagation(); skipNext(); }} className="p-2 text-zinc-300 hover:text-white"><SkipForward className="w-5 h-5" /></button>
            {canDiscover && (
              <button onClick={listenInFull} className="hidden sm:inline-flex py-1.5 px-3 rounded-full text-xs font-bold bg-[#00F2FE]/15 text-[#00F2FE] border border-[#00F2FE]/30" data-testid="player-discovery">
                Écouter en entier
              </button>
            )}
            {isPreview && (
              <button onClick={(e) => { e.stopPropagation(); setBuying(current); }} className="hidden sm:inline-flex btn-primary py-1.5 px-3 text-xs" data-testid="player-buy">
                Acheter · ${Number(current.price).toFixed(2)}
              </button>
            )}
            <button onClick={handleClose} className="p-2 text-zinc-400 hover:text-white" aria-label="close-player" data-testid="audio-player-close"><X className="w-5 h-5" /></button>
          </div>

          <div className="hidden md:flex items-center gap-2 flex-[1.5]">
            <span className="text-xs text-zinc-500 tabular-nums">{formatTime(progress * total)}</span>
            <div className="relative h-1.5 flex-1 bg-zinc-800 rounded-full cursor-pointer group" onClick={(e) => {
              e.stopPropagation();
              const r = e.currentTarget.getBoundingClientRect();
              seek((e.clientX - r.left) / r.width);
            }}>
              <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#FF0050] to-[#00F2FE]" style={{ width: `${progress * 100}%` }} />
            </div>
            <span className="text-xs text-zinc-500 tabular-nums">{formatTime(total)}</span>
          </div>

          <div className="hidden lg:flex items-center gap-2 w-32">
            <Volume2 className="w-4 h-4 text-zinc-400" />
            <input type="range" min="0" max="1" step="0.01" value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} onClick={e => e.stopPropagation()} className="w-full accent-[#FF0050]" />
          </div>
        </div>
      </div>

      {/* Full Player Modal */}
      {showFull && (
        <div className="fixed inset-0 z-[60] bg-black animate-in fade-in slide-in-from-bottom-10 duration-300 flex flex-col">
          <div className="flex items-center justify-between p-6">
            <button onClick={() => setShowFull(false)} className="p-2 rounded-full bg-white/10 text-white"><ChevronDown className="w-6 h-6" /></button>
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t.audio.nowPlaying || "LECTURE EN COURS"}</p>
              <p className="text-sm font-medium text-white">{current.title}</p>
            </div>
            <div className="w-10" />
          </div>

          <div className="flex-1 flex flex-col md:flex-row items-center justify-center px-8 gap-8 md:gap-16 max-w-6xl mx-auto w-full">
            <div className="w-full max-w-[320px] md:max-w-[450px] aspect-square relative group">
              <img src={resolveImg(current.cover)} alt={current.title} className="w-full h-full object-cover rounded-2xl shadow-2xl shadow-purple-500/20" />
              {isPreview && <div className="absolute top-4 right-4 bg-[#FF0050] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">{t.audio.previewLabel}</div>}
            </div>

            <div className="flex-1 w-full flex flex-col">
              <div className="mb-8">
                <h2 className="text-3xl md:text-5xl font-black text-white mb-2">{current.title}</h2>
                <p className="text-xl text-[#00F2FE] font-medium">{current.artist_name}</p>
              </div>

              <div className="space-y-4">
                <div className="relative h-2 w-full bg-white/10 rounded-full cursor-pointer group" onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  seek((e.clientX - r.left) / r.width);
                }}>
                  <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#FF0050] to-[#8A2BE2]" style={{ width: `${progress * 100}%` }} />
                  <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-xl opacity-0 group-hover:opacity-100 transition-opacity" style={{ left: `${progress * 100}%`, marginLeft: '-8px' }} />
                </div>
                <div className="flex justify-between text-xs font-medium text-zinc-500 tabular-nums">
                  <span>{formatTime(progress * total)}</span>
                  <span>{formatTime(total)}</span>
                </div>
                {isPreview && (
                  <div className="flex items-center justify-between gap-3 rounded-2xl bg-[#FF0050]/10 border border-[#FF0050]/20 px-4 py-3" data-testid="full-player-preview">
                    <p className="text-sm text-zinc-200">Extrait choisi par l'artiste. Achète la chanson pour l'écouter en entier.</p>
                    <button onClick={() => setBuying(current)} className="btn-primary py-2 px-4 text-xs whitespace-nowrap">Acheter · ${Number(current.price).toFixed(2)}</button>
                  </div>
                )}
                {canDiscover && (
                  <button onClick={listenInFull} className="w-full py-3 rounded-2xl text-sm font-bold bg-[#00F2FE]/15 text-[#00F2FE] border border-[#00F2FE]/30" data-testid="full-player-discovery">
                    🎧 Écouter en entier, gratuitement
                  </button>
                )}
              </div>

              <div className="flex items-center justify-center gap-8 mt-8">
                <button onClick={skipPrev} className="p-3 text-white/70 hover:text-white transition-colors"><SkipBack className="w-8 h-8" /></button>
                <button onClick={handleStop} className="p-3 text-white/70 hover:text-white transition-colors"><Square className="w-8 h-8" /></button>
                <button onClick={togglePlay} className="w-20 h-20 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 active:scale-95 transition-all shadow-xl shadow-white/10">
                  {playing ? <Pause className="w-10 h-10" /> : <Play className="w-10 h-10 ml-1" />}
                </button>
                <button onClick={skipNext} className="p-3 text-white/70 hover:text-white transition-colors"><SkipForward className="w-8 h-8" /></button>
              </div>

              {current.lyrics && (
                <div className="mt-12 p-6 rounded-2xl bg-white/5 border border-white/10 flex-1 overflow-y-auto max-h-[200px] custom-scrollbar">
                  <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4 flex items-center gap-2"><Music className="w-3 h-3" /> {t.audio.lyrics || "Paroles"}</h3>
                  <p className="text-lg text-white/90 leading-relaxed whitespace-pre-wrap font-medium">{current.lyrics}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {buying && <PurchaseModal track={buying} onClose={() => setBuying(null)} />}
    </>
  );
}

function formatTime(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}
